<template>
  <section
    ref="sectionEl"
    id="process"
    class="py-28 bg-white dark:bg-dark transition-colors duration-300"
    :class="{ 'is-visible': isVisible }"
  >
    <div class="container mx-auto px-4">

      <!-- Header -->
      <div class="max-w-3xl mx-auto text-center mb-20 anim-el d1">
        <p class="text-[13px] uppercase tracking-[0.2em] text-gray-400 dark:text-gray-600 font-medium mb-4">
          {{ t('process.title') }}
        </p>
        <h2 class="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white tracking-tight leading-[1.1] transition-colors duration-300">
          Come trasformiamo le tue idee<br />
          <span class="gradient-text">in realtà operativa</span>
        </h2>
      </div>

      <!-- Timeline steps -->
      <div class="max-w-3xl mx-auto mb-20 space-y-0">
        <div
          v-for="(step, index) in processSteps"
          :key="index"
          class="relative flex gap-6 md:gap-10 anim-el"
          :class="'d' + (index + 2)"
        >
          <!-- Number + connector line -->
          <div class="flex flex-col items-center">
            <div class="w-12 h-12 rounded-full bg-gray-100 dark:bg-white/[0.06] border border-gray-200 dark:border-white/[0.1] flex items-center justify-center text-sm font-bold text-gray-900 dark:text-white flex-shrink-0 transition-colors duration-300">
              {{ index + 1 }}
            </div>
            <div
              v-if="index < processSteps.length - 1"
              class="w-px flex-1 bg-gray-200 dark:bg-white/[0.06] my-2 transition-colors duration-300"
            ></div>
          </div>

          <!-- Content + illustration -->
          <div class="pb-14 flex-1" :class="{ 'pb-0': index === processSteps.length - 1 }">
            <h3 class="text-xl font-semibold text-gray-900 dark:text-white mb-2 transition-colors duration-300">
              {{ step.title }}
            </h3>
            <p class="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mb-5 max-w-lg">
              {{ step.description }}
            </p>

            <!-- Step 1: Discovery — animated checklist -->
            <div
              v-if="index === 0"
              class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] p-4 max-w-xs transition-colors duration-300"
            >
              <p class="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 mb-3 font-medium">Report Strategico</p>
              <div class="space-y-2.5">
                <div
                  v-for="(item, i) in discoveryItems"
                  :key="item"
                  class="flex items-center gap-2.5 check-item"
                  :style="{ '--item-delay': `${i * 0.22 + 0.6}s` }"
                >
                  <div class="w-4 h-4 rounded border border-blue-500/30 bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <Check class="w-2.5 h-2.5 text-blue-500" />
                  </div>
                  <span class="text-[11px] text-gray-600 dark:text-gray-400">{{ item }}</span>
                </div>
              </div>
            </div>

            <!-- Step 2: Prototype — wireframe browser -->
            <div
              v-else-if="index === 1"
              class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] overflow-hidden max-w-xs transition-colors duration-300"
            >
              <div class="flex items-center gap-1.5 px-3 py-2 bg-gray-100 dark:bg-white/[0.04] border-b border-gray-200 dark:border-white/[0.06]">
                <div class="w-2 h-2 rounded-full bg-red-400/60"></div>
                <div class="w-2 h-2 rounded-full bg-yellow-400/60"></div>
                <div class="w-2 h-2 rounded-full bg-green-400/60"></div>
                <div class="flex-1 h-3.5 rounded bg-white dark:bg-white/[0.06] mx-3"></div>
              </div>
              <div class="p-3 space-y-2">
                <div class="h-2 w-16 rounded bg-gray-300 dark:bg-white/[0.12] proto-el" style="--proto-delay: 0.5s"></div>
                <div class="h-12 rounded bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/10 proto-el flex items-center justify-center" style="--proto-delay: 0.7s">
                  <div class="h-2 w-20 rounded bg-blue-400/40"></div>
                </div>
                <div class="grid grid-cols-3 gap-1.5 proto-el" style="--proto-delay: 0.9s">
                  <div v-for="i in 3" :key="i" class="h-8 rounded bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.04]"></div>
                </div>
                <div class="rounded-lg bg-blue-500/5 border border-blue-500/10 p-2 proto-el" style="--proto-delay: 1.1s">
                  <div class="flex gap-2 items-center">
                    <div class="w-4 h-4 rounded bg-blue-500/20"></div>
                    <div class="flex-1 h-1.5 rounded bg-blue-500/20"></div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 3: Development — code editor -->
            <div
              v-else-if="index === 2"
              class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-900 dark:bg-black/50 p-4 max-w-xs font-mono text-[11px] leading-relaxed overflow-hidden"
            >
              <div class="flex gap-1 mb-3">
                <div class="w-2 h-2 rounded-full bg-red-400/60"></div>
                <div class="w-2 h-2 rounded-full bg-yellow-400/60"></div>
                <div class="w-2 h-2 rounded-full bg-green-400/60"></div>
              </div>
              <div class="code-line" style="--code-delay: 0.5s">
                <span class="text-purple-400">const</span>
                <span class="text-blue-300"> ai</span>
                <span class="text-gray-400"> = </span>
                <span class="text-yellow-300">new</span>
                <span class="text-green-400"> CiobaiAI(config)</span>
              </div>
              <div class="code-line" style="--code-delay: 0.9s">
                <span class="text-blue-300">ai</span><span class="text-gray-400">.</span><span class="text-yellow-300">train</span><span class="text-gray-400">(</span><span class="text-green-400">yourData</span><span class="text-gray-400">)</span>
              </div>
              <div class="code-line" style="--code-delay: 1.3s">
                <span class="text-blue-300">ai</span><span class="text-gray-400">.</span><span class="text-yellow-300">integrate</span><span class="text-gray-400">(</span><span class="text-orange-400">'CRM, ERP'</span><span class="text-gray-400">)</span>
              </div>
              <div class="code-line" style="--code-delay: 1.7s">
                <span class="text-blue-300">ai</span><span class="text-gray-400">.</span><span class="text-yellow-300">deploy</span><span class="text-gray-400">(</span><span class="text-orange-400">'production'</span><span class="text-gray-400">)</span>
              </div>
              <div class="code-line" style="--code-delay: 2.1s">
                <span class="text-green-400">// ✓ Sistema attivo </span><span class="text-gray-500">→ 847 task/g</span>
              </div>
            </div>

            <!-- Step 4: Launch — success dashboard -->
            <div
              v-else
              class="rounded-xl border border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-white/[0.02] p-4 max-w-xs transition-colors duration-300"
            >
              <div class="flex items-center gap-3 mb-4">
                <div class="w-9 h-9 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                  <Rocket class="w-4 h-4 text-green-500" />
                </div>
                <div>
                  <p class="text-[11px] font-bold text-green-600 dark:text-green-400">Sistema Online!</p>
                  <p class="text-[9px] text-gray-400 dark:text-gray-500">Performance in crescita</p>
                </div>
                <div class="ml-auto flex gap-0.5 items-end">
                  <div class="w-1.5 h-3 rounded-sm bg-green-500/30"></div>
                  <div class="w-1.5 h-5 rounded-sm bg-green-500/50"></div>
                  <div class="w-1.5 h-7 rounded-sm bg-green-500/70"></div>
                  <div class="w-1.5 h-9 rounded-sm bg-green-500"></div>
                </div>
              </div>
              <div class="space-y-2">
                <div
                  v-for="(item, i) in launchItems"
                  :key="item"
                  class="flex items-center gap-2 launch-item"
                  :style="{ '--item-delay': `${i * 0.25 + 0.6}s` }"
                >
                  <div class="w-4 h-4 rounded-full bg-green-500/10 flex items-center justify-center flex-shrink-0">
                    <Check class="w-2.5 h-2.5 text-green-500" />
                  </div>
                  <span class="text-[11px] text-gray-500 dark:text-gray-400">{{ item }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- CTA -->
      <div class="text-center anim-el d6">
        <router-link
          to="/contact"
          class="inline-flex items-center h-12 px-7 bg-gray-900 dark:bg-white text-white dark:text-dark font-semibold text-[15px] rounded-xl hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors group"
        >
          {{ t('process.cta') }}
          <ArrowRight class="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
        </router-link>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { Check, ArrowRight, Rocket } from 'lucide-vue-next'

const { t } = useI18n()

const sectionEl = ref(null)
const isVisible = ref(false)
let observer = null

onMounted(() => {
  observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) isVisible.value = true
    })
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' })
  if (sectionEl.value) observer.observe(sectionEl.value)
})

onUnmounted(() => {
  if (observer) observer.disconnect()
})

const processSteps = computed(() => [
  { title: t('process.steps.discovery.title'), description: t('process.steps.discovery.description') },
  { title: t('process.steps.prototype.title'), description: t('process.steps.prototype.description') },
  { title: t('process.steps.development.title'), description: t('process.steps.development.description') },
  { title: t('process.steps.deployment.title'), description: t('process.steps.deployment.description') }
])

const discoveryItems = [
  'Analisi dei processi aziendali',
  'Definizione degli obiettivi',
  'Identificazione opportunità IA',
  'Report strategico personalizzato'
]

const launchItems = [
  'Deploy in produzione completato',
  'Chatbot / automazioni attivi',
  'Team formato e pronto',
  'Monitoraggio continuo attivo'
]
</script>

<style scoped>
.gradient-text {
  background: linear-gradient(135deg, #60a5fa 0%, #3b82f6 50%, #2563eb 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ─── Entrance animations ─── */
.anim-el {
  opacity: 0;
  transform: translateY(24px);
  transition: opacity 0.7s cubic-bezier(.22,1,.36,1), transform 0.7s cubic-bezier(.22,1,.36,1);
}
.is-visible .anim-el { opacity: 1; transform: translateY(0); }
.d1 { transition-delay: 0.05s; }
.d2 { transition-delay: 0.10s; }
.d3 { transition-delay: 0.15s; }
.d4 { transition-delay: 0.20s; }
.d5 { transition-delay: 0.25s; }
.d6 { transition-delay: 0.30s; }

/* ─── Discovery checklist items ─── */
.check-item {
  opacity: 0;
  transform: translateX(-8px);
  transition: opacity 0.4s ease, transform 0.4s ease;
  transition-delay: var(--item-delay, 0s);
}
.is-visible .check-item {
  opacity: 1;
  transform: translateX(0);
}

/* ─── Prototype wireframe elements ─── */
.proto-el {
  opacity: 0;
  transform: translateY(6px);
  transition: opacity 0.45s ease, transform 0.45s ease;
  transition-delay: var(--proto-delay, 0s);
}
.is-visible .proto-el {
  opacity: 1;
  transform: translateY(0);
}

/* ─── Code editor lines ─── */
.code-line {
  opacity: 0;
  transition: opacity 0.4s ease;
  transition-delay: var(--code-delay, 0s);
}
.is-visible .code-line {
  opacity: 1;
}

/* ─── Launch items ─── */
.launch-item {
  opacity: 0;
  transform: translateX(-8px);
  transition: opacity 0.4s ease, transform 0.4s ease;
  transition-delay: var(--item-delay, 0s);
}
.is-visible .launch-item {
  opacity: 1;
  transform: translateX(0);
}
</style>
